import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { AvChartTone } from '../../../../../Shared/Types/av-chart-tone.type';
import { COMMON_IMPORTS } from '../../../../../Shared/common-imports';
import { scaleRatio } from '../../../../../Shared/Services/count-up-value.util';
import { TranslationService } from '../../../../../Shared/Services/translation.service';
import { formatMonthName } from '../../../Shared/monitoring-month-name.util';

@Component({
  selector: 'app-monitoring-delta',
  standalone: true,
  imports: [COMMON_IMPORTS],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './monitoring-delta.component.html',
  styleUrl: './monitoring-delta.component.scss',
})
export class MonitoringDeltaComponent {
  readonly percent = input.required<number>();
  readonly lowerIsBetter = input<boolean>(false);
  readonly progress = input<number>(1);
  readonly previousFrom = input<string | null>(null);
  readonly unit = input<'percent' | 'points'>('percent');

  protected readonly language = inject(TranslationService).currentLanguage;

  protected readonly previousMonth = computed(() => {
    const start = this.previousFrom();
    return start ? formatMonthName(this.language(), new Date(start)) : null;
  });

  protected readonly value = computed(() => (this.percent() === 0 ? 0 : this.percent()));

  protected readonly fraction = computed(() => scaleRatio(this.value(), this.progress()) / 100);

  protected readonly points = computed(() => scaleRatio(this.value(), this.progress()));

  protected readonly sign = computed(() => (this.value() > 0 ? '+' : ''));

  protected readonly icon = computed(() => {
    if (this.value() > 0) return 'trending_up';
    if (this.value() < 0) return 'trending_down';
    return 'trending_flat';
  });

  protected readonly tone = computed<AvChartTone>(() => {
    if (this.value() === 0) return 'muted';
    const improved = this.lowerIsBetter() ? this.value() < 0 : this.value() > 0;
    return improved ? 'success' : 'error';
  });
}
